/**
 * The line-item Description field, with the price book behind it.
 *
 * Type to narrow, pick to fill Description / Rate / Revenue Code / Unit — or
 * keep typing and save whatever you wrote. It is a combobox and NOT a menu, and
 * that is the whole point: a hand billing something the sheet does not carry
 * has to be able to, both editors block the save on a blank description, and
 * that path has already been broken once (frontend #112 and #113, June 2026).
 * Replacing the text field with a chooser would break it again, permanently.
 *
 * ONE COMPONENT, BOTH EDITORS. `NewTicketScreen` (office) and
 * `FieldTicketsScreen` (field) are near-identical copy-paste of each other —
 * same line-item interface, same handlers, same markup down to the class
 * strings, differing only in which surface colour the inputs sit on. That
 * duplication is how they drifted before, so this ships once and both render it.
 *
 * Interaction follows the rig-name autocomplete in `NewJobScreen`, which is the
 * house pattern: open on focus, close on outside click, and commit an option on
 * mousedown with the default prevented so the input's blur cannot fire first and
 * close the menu out from under the click.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, CloudOff, Loader, Link2Off } from 'lucide-react';
import {
  type RateSheetItem,
  filterRateSheetItems,
  groupRateSheetItems,
  capGroupedItems,
  rateSheetItemUnitText,
} from '../../lib/rate-sheet-items';
import { fmtCurrency } from '../../lib/display-labels';
import { findExactBookItem, type PriceBookLinkState } from '../../lib/price-book-link';

/**
 * Why the price book is or is not usable right now.
 *
 * A separate value for each reason, because they need different words. "No
 * items" and "could not load" and "you are offline" all look like an empty menu
 * and only one of them means the tenant has no rates — CLAUDE.md's rule that a
 * refusal is not an empty state.
 */
export type RateSheetItemsState =
  /** Loaded, with rows to offer. */
  | 'ready'
  /** The request is in flight. */
  | 'loading'
  /** No network. The request was never made. */
  | 'offline'
  /** The request was made and failed. */
  | 'failed'
  /** The job has no customer, so there is no set of sheets that may bill it. */
  | 'no-customer'
  /** Loaded successfully and the tenant genuinely has no priced items. */
  | 'empty';

interface RateSheetItemPickerProps {
  /** The line item's description. Free text; this component never rejects it. */
  value: string;
  /** Fired on every keystroke. Free text is committed exactly as typed. */
  onChange: (description: string) => void;
  /** Fired when a price book row is chosen. */
  onSelect: (item: RateSheetItem) => void;
  items: RateSheetItem[];
  state: RateSheetItemsState;
  /** `jobs.rate_sheet_id`. Hoists that sheet's group; never removes the others. */
  preferredSheetId?: string | null;
  /** Which of the two editors this is rendering in. */
  surface: 'card' | 'dark';
  placeholder?: string;
  /** The row failed the editors' blank-description check. */
  invalid?: boolean;
  /**
   * Accessible name for the field. The editors' own <label> elements are not
   * associated with their controls, so without this the input has no name at
   * all — to a screen reader, and to anything else that asks.
   */
  ariaLabel?: string;
  /**
   * The line's relationship to the price book (lib/price-book-link.ts). When
   * given, an `unlinked` line with a description shows a marker, and every
   * unlinked or off-book line shows the Off book toggle. Never a block: the
   * marker is information, and a line saves either way — online or not.
   */
  linkState?: PriceBookLinkState;
  /** The Off book toggle. `true` also drops any link the editor holds. */
  onOffBookChange?: (offBook: boolean) => void;
  /**
   * Fired on leaving the field when the typed description names exactly one
   * book item (`findExactBookItem`). Links only — never re-prices the row.
   */
  onAutoLink?: (item: RateSheetItem) => void;
  /** The row's unit, so an exact name at a different unit is not linked. */
  rowUnit?: string | null;
}

/**
 * The two editors sit their inputs on different surfaces. Whole class strings,
 * never assembled from pieces — the scanner that builds the stylesheet reads
 * source as text and cannot resolve a name that is put together at runtime.
 */
const SURFACE = {
  card: { input: 'bg-fieldiq-card', menu: 'bg-fieldiq-card', row: 'hover:bg-fieldiq-border/40' },
  dark: { input: 'bg-fieldiq-dark', menu: 'bg-fieldiq-dark', row: 'hover:bg-fieldiq-border/40' },
} as const;

/**
 * How many rows the menu will draw at once.
 *
 * TNT carries 311 reachable items and a phone renders that list badly. Whatever
 * this leaves out is COUNTED AND SHOWN — a menu that silently stops at 60 reads
 * as "the book only has 60", and a hand who cannot find their item stops looking
 * instead of typing one more word.
 */
const MAX_ROWS = 60;

export default function RateSheetItemPicker({
  value,
  onChange,
  onSelect,
  items,
  state,
  preferredSheetId,
  surface,
  placeholder,
  invalid,
  ariaLabel,
  linkState,
  onOffBookChange,
  onAutoLink,
  rowUnit,
}: RateSheetItemPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const skin = SURFACE[surface];
  // A combobox has to name the list it controls, and a ticket renders one of
  // these per line item, so the id cannot be a constant.
  const listboxId = `${useId()}-price-book`;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // The description doubles as the search term, so a row already filled in from
  // an earlier pick narrows to itself rather than re-listing the whole book.
  const { groups, hidden, matched } = useMemo(() => {
    if (state !== 'ready') return { groups: [], hidden: 0, matched: 0 };
    const found = filterRateSheetItems(items, value);
    const capped = capGroupedItems(groupRateSheetItems(found, preferredSheetId), MAX_ROWS);
    return { groups: capped.groups, hidden: capped.hidden, matched: found.length };
  }, [items, value, state, preferredSheetId]);

  const commit = (item: RateSheetItem) => {
    onSelect(item);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        aria-label={ariaLabel}
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Only an unlinked line that is not off book: a picked link is never
          // replaced by typing, and off book is an answer the office gave.
          if (!onAutoLink || linkState !== 'unlinked' || state !== 'ready') return;
          const match = findExactBookItem(items, value, rowUnit);
          if (match) onAutoLink(match);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && state === 'ready'}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className={`w-full ${skin.input} rounded px-2 py-1 text-xs border outline-none ${
          invalid
            ? 'border-red-500/60 text-red-400 placeholder-red-400/50'
            : 'border-fieldiq-border'
        }`}
      />

      {(linkState === 'unlinked' || linkState === 'off_book') && onOffBookChange && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
          {linkState === 'unlinked' && (value ?? '').trim() !== '' && (
            <span data-testid="price-book-unlinked" className="flex items-center gap-1 text-fieldiq-muted">
              <Link2Off size={11} aria-hidden="true" className="text-fieldiq-accent-signal" />
              Not linked to the price book
            </span>
          )}
          <label className="flex items-center gap-1 text-fieldiq-muted cursor-pointer">
            <input
              type="checkbox"
              checked={linkState === 'off_book'}
              onChange={(e) => onOffBookChange(e.target.checked)}
              className="accent-fi-amber"
            />
            Off book
          </label>
        </div>
      )}

      {open && state !== 'ready' && (
        <p className="text-[11px] text-fieldiq-muted mt-0.5 flex items-center gap-1">
          {state === 'loading' && (
            <>
              <Loader size={11} className="animate-spin" /> Loading the price book…
            </>
          )}
          {/* Never a disabled field and never a silent empty menu: a hand in a
              dead zone has to be told the prices are not here AND that typing
              still works, or the screen reads as broken. */}
          {state === 'offline' && (
            <>
              <CloudOff size={11} /> Price book isn't available offline — type the description.
            </>
          )}
          {state === 'failed' && <>Price book couldn't be loaded — type the description.</>}
          {state === 'no-customer' && <>No client on this job, so no rate sheet applies.</>}
          {state === 'empty' && <>No priced items on this company's rate sheets.</>}
        </p>
      )}

      {open && state === 'ready' && (
        <div
          id={listboxId}
          className={`absolute z-50 left-0 right-0 mt-1 ${skin.menu} border border-fieldiq-border rounded-lg shadow-xl max-h-72 overflow-y-auto`}
          role="listbox"
        >
          {matched === 0 ? (
            // Free text is not a fallback here, it is a supported answer. Say so,
            // rather than showing an empty box that reads as a dead control.
            <p className="px-3 py-2.5 text-xs text-fieldiq-muted">
              Nothing in the price book matches. It'll be saved exactly as you typed it.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.sheetId}>
                <div className="sticky top-0 px-3 py-1.5 bg-fieldiq-dark/95 border-b border-fieldiq-border">
                  <p className="text-[10px] font-bold text-fieldiq-accent tracking-wider uppercase truncate">
                    {group.sheetName}
                    {group.preferred && (
                      <span className="ml-1.5 text-fieldiq-muted normal-case font-normal tracking-normal">
                        · this job's sheet
                      </span>
                    )}
                  </p>
                </div>
                {group.items.map((item) => {
                  const unitText = rateSheetItemUnitText(item);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className={`w-full text-left px-3 py-2 ${skin.row} transition-colors border-b border-fieldiq-border/40`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        commit(item);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs text-white font-medium">{item.serviceName}</span>
                        <span className="text-xs text-green-400 font-semibold whitespace-nowrap">
                          {fmtCurrency(item.price, item.currency)}
                        </span>
                      </div>
                      {(unitText || item.revenueCode) && (
                        <p className="text-[10px] text-fieldiq-muted mt-0.5">
                          {[unitText, item.revenueCode].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {/* The book's own detail note. Shown, never used as the
                          label — it is nullable and it is not a name. */}
                      {item.description && (
                        <p className="text-[10px] text-fieldiq-muted/80 mt-0.5 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}

          {hidden > 0 && (
            <p className="px-3 py-2 text-[11px] text-fieldiq-muted flex items-center gap-1 border-t border-fieldiq-border">
              <Search size={11} /> {hidden} more {hidden === 1 ? 'item' : 'items'} — keep typing to
              narrow.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
